/** Formatadores do painel de Analytics. Valor ausente vira traco, nunca zero. */

export const DASH = '-';

const intFmt = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const decFmt = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const brlFmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

export type Nullable = number | null | undefined;

const isNum = (v: Nullable): v is number => typeof v === 'number' && Number.isFinite(v);

export function formatInt(v: Nullable): string {
    return isNum(v) ? intFmt.format(v) : DASH;
}

/** 3,5 (uma casa, virgula decimal). */
export function formatDecimal(v: Nullable): string {
    return isNum(v) ? decFmt.format(v) : DASH;
}

export function formatBRL(v: Nullable): string {
    return isNum(v) ? brlFmt.format(v) : DASH;
}

export function formatPct(v: Nullable): string {
    return isNum(v) ? `${decFmt.format(v)}%` : DASH;
}

/** 2,5 dias, 4 dias, 1 dia. */
export function formatDays(v: Nullable): string {
    if (!isNum(v)) return DASH;
    return `${decFmt.format(v)} ${v === 1 ? 'dia' : 'dias'}`;
}

function sign(v: number): string {
    if (v > 0) return '+';
    if (v < 0) return '-';
    return '';
}

/** +1, -2 ou 0. */
export function formatSignedInt(v: Nullable): string {
    return isNum(v) ? `${sign(v)}${intFmt.format(Math.abs(v))}` : DASH;
}

/** +25%, -40% ou 0%. */
export function formatSignedPct(v: Nullable): string {
    return isNum(v) ? `${sign(v)}${decFmt.format(Math.abs(v))}%` : DASH;
}

/** 2026-10-15 -> 15/10/2026. */
export function formatDayBR(day: string | null | undefined): string {
    if (!day || !/^\d{4}-\d{2}-\d{2}/.test(day)) return DASH;
    const [y, m, d] = day.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
}

/** Dia local de um instante ISO, no fuso informado. */
export function formatInstantDayBR(iso: string | null | undefined, timeZone: string): string {
    if (!iso) return DASH;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return DASH;
    return new Intl.DateTimeFormat('pt-BR', { timeZone, day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

export type Granularity = 'day' | 'week' | 'month';

/** Rotulo do eixo X: mes vira "mar de 26", dia e semana viram "15/10". */
export function formatTrendLabel(label: string, granularity: Granularity): string {
    if (granularity === 'month') {
        const [y, m] = label.split('-');
        const month = MONTHS[Number(m) - 1];
        return month ? `${month} de ${y.slice(2)}` : label;
    }
    const [, m, d] = label.split('-');
    return m && d ? `${d}/${m}` : label;
}

const GRANULARITY_UNITS: Record<Granularity, [string, string]> = {
    day: ['dia', 'dias'],
    week: ['semana', 'semanas'],
    month: ['mês', 'meses'],
};

/** "8 meses ate 15/10/2026". */
export function formatTrendRange(count: number, granularity: Granularity, endDay: string): string {
    const [one, many] = GRANULARITY_UNITS[granularity];
    const unit = count === 1 ? one : many;
    return `${count} ${unit} até ${formatDayBR(endDay)}`;
}
